import { useState } from "react";
import { AuthPageLayout } from "./auth-page-layout";
import { Button, FormError, Icons, Spinner } from "@/components/ui";

type VerifyStatus = "verifying" | "success" | "error";

interface VerifyEmailPageProps {
  /** Initial status to display. Defaults to "success". */
  initialStatus?: VerifyStatus;
}

const MOCK_ERROR = "This verification link has expired.";

export function VerifyEmailPage({
  initialStatus = "success",
}: VerifyEmailPageProps) {
  const [status] = useState<VerifyStatus>(initialStatus);

  return (
    <AuthPageLayout>
      {status === "verifying" && (
        <div
          className="flex flex-col items-center gap-5 py-12"
          data-testid="verify-loading"
        >
          <Spinner className="h-8 w-8" />
          <p className="text-[15px] text-secondary">
            Verifying your email...
          </p>
        </div>
      )}

      {status === "success" && (
        <div
          className="flex flex-col items-center gap-5 text-center"
          data-testid="verify-success"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-glass-stroke bg-glass-fill">
            <Icons.CheckCircle className="h-7 w-7 text-accent" />
          </div>
          <h2 className="text-2xl font-semibold text-primary">
            Email verified
          </h2>
          <p className="text-[15px] leading-normal text-secondary">
            Your email has been verified successfully.
            {"\n"}You can now sign in to your account.
          </p>
          <Button
            variant="secondary"
            size="lg"
            onClick={() => console.log("[workshop] Navigate to login")}
            className="w-full liquid-glass-distortion shadow-glass border-glass-stroke-light font-semibold text-accent"
            data-testid="verify-login-link"
          >
            Sign in
          </Button>
        </div>
      )}

      {status === "error" && (
        <div
          className="flex flex-col items-center gap-5 text-center"
          data-testid="verify-error"
        >
          <div className="flex h-16 w-16 items-center justify-center rounded-full border border-glass-stroke bg-glass-fill">
            <Icons.AlertTriangle className="h-7 w-7 text-danger" />
          </div>
          <h2 className="text-2xl font-semibold text-primary">
            Verification failed
          </h2>
          <FormError message={MOCK_ERROR} className="mt-0" />
          <p className="text-[15px] leading-normal text-secondary">
            The link may have expired.{" "}
            <button
              type="button"
              onClick={() => console.log("[workshop] Navigate to login")}
              className="font-semibold text-accent hover:underline"
              data-testid="verify-error-login-link"
            >
              Sign in
            </button>{" "}
            to request a new verification email.
          </p>
        </div>
      )}
    </AuthPageLayout>
  );
}
